import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation, useQuery } from '@apollo/client/react'
// graphql
import { PLAN_UPDATE_MUTATION } from 'graphql/plan/mutations'
import { PLAN_DETAILS_TREE_QUERY, PLANS_DETAILS_QUERY } from 'graphql/plan/queries'
// constants
import { PLAN_VIEW_MODE } from 'constants/plan'
// types
import { TRefetch } from 'types/refetch'
import { NetworkStatus } from '@apollo/client'
import { IPlan, IPlansResponse, IPlansQueryArgs, IPlanItemsQueryArgs, IPlanItemsResponse, IFieldFilter } from 'types/graphql'
// utils
import get from 'lodash/get'
import { getPlanColumnVariables } from 'utils/planColumns'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

type Props = React.PropsWithChildren<unknown>

type PlanQueryResponse = {
  plans: IPlansResponse
}

type TreeQueryResponse = {
  planItems: IPlanItemsResponse
}

interface IContext {
  plan: IPlan
  lockPlan: (value: boolean) => Promise<void>
  isLoading: boolean
  isSubPlan: boolean
  treeQuery: TreeQueryResponse
  treeQueryLoading: boolean
  treeQueryRefetch: TRefetch<TreeQueryResponse, IPlanItemsQueryArgs>
  setPlanVisibility: (input: Record<'private', boolean>) => Promise<void>
  planDetailsRefetch: TRefetch<PlanQueryResponse, IPlansQueryArgs>
  fieldFilters: IFieldFilter[]
  setFieldFilters: (filters: IFieldFilter[]) => void
  showArchivedPlanItems: boolean
  toggleShowArchivedPlanItems: () => void
  isSidebarCollapsed: boolean
  toggleSidebarCollapsed: () => void
}

export const PlanEditPageContext = React.createContext<IContext>({} as any)

const PlanEditPageAPI: React.FC<Props> = ({ children }) => {
  const router = useRouter()

  const [fieldFilters, setFieldFilters] = React.useState<IFieldFilter[]>([])
  const [showArchivedPlanItems, setShowArchivedPlanItems] = React.useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false)

  const toggleShowArchivedPlanItems = () => setShowArchivedPlanItems((prev) => !prev)
  const toggleSidebarCollapsed = () => setIsSidebarCollapsed((prev) => !prev)

  const {
    data: plansQuery,
    refetch,
    loading: plansLoading,
    networkStatus: plansNetworkStatus
  } = useQuery<PlanQueryResponse>(PLANS_DETAILS_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: { id: router.query.id }
    }
  })

  const isLoading = plansLoading && (plansNetworkStatus === NetworkStatus.loading || plansNetworkStatus === NetworkStatus.setVariables)

  const {
    data: treeQueryData,
    previousData: treePreviousData,
    refetch: treeQueryRefetch,
    loading: treeQueryLoading,
    networkStatus
  } = useQuery<TreeQueryResponse>(PLAN_DETAILS_TREE_QUERY, {
    skip: !router.query.id || !plansQuery,
    fetchPolicy: 'network-only',
    variables: {
      ...getPlanColumnVariables(get(plansQuery, 'plans.data[0].columnSettings')),
      filter: {
        planId: router.query.id,
        referenced: router.query.planViewMode === PLAN_VIEW_MODE.PLAN ? false : null,
        ...(fieldFilters?.length && {
          fieldFilters: fieldFilters.map(({ id, fieldType, action, value, systemName }) => ({
            fieldType,
            action,
            value,
            ...(systemName ? { systemName } : { id })
          }))
        })
      }
    }
  })

  const treeQuery = treeQueryData ?? treePreviousData

  const [planUpdateMutation] = useMutation(PLAN_UPDATE_MUTATION)

  const plan = get(plansQuery, 'plans.data[0]')
  const isSubPlan = !!get(plan, 'parentPlan')

  const setPlanVisibility = async (input) => {
    try {
      await treeQueryRefetch()

      await planUpdateMutation({
        variables: {
          id: plan.id,
          input
        }
      })

      refetch()
    } catch (errors) {
      console.log('[PlanEditPageAPI.setPlanVisibility]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const lockPlan = async (value: boolean) => {
    try {
      await planUpdateMutation({
        variables: {
          id: plan.id,
          input: {
            locked: value
          }
        }
      })

      await refetch()
    } catch (errors) {
      console.log('[PlanEditPageAPI.lockPlan]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const value = {
    plan,
    lockPlan,
    isLoading,
    isSubPlan,
    treeQuery,
    treeQueryLoading: treeQueryLoading && networkStatus === NetworkStatus.loading,
    treeQueryRefetch,
    setPlanVisibility,
    planDetailsRefetch: refetch,
    fieldFilters,
    setFieldFilters,
    showArchivedPlanItems,
    toggleShowArchivedPlanItems,
    isSidebarCollapsed,
    toggleSidebarCollapsed,
  }

  return <PlanEditPageContext.Provider value={value}>{children}</PlanEditPageContext.Provider>
}

export default PlanEditPageAPI
